import { create } from 'zustand';
import type { LogEntry, SeverityKind } from './types';

const MAX_LOGS = 500;

export interface SeverityFilterState {
  info: boolean;
  warn: boolean;
  error: boolean;
}

interface LogverseState {
  logs: LogEntry[];
  paused: boolean;
  selectedLog: LogEntry | null;
  activeService: string | null;
  severityFilter: SeverityFilterState;
  timelinePosition: number;
  fps: number;
  addLog: (log: LogEntry) => void;
  togglePause: () => void;
  setSelectedLog: (log: LogEntry | null) => void;
  setActiveService: (service: string | null) => void;
  toggleSeverity: (severity: SeverityKind) => void;
  showOnlySeverity: (severity: SeverityKind) => void;
  setTimelinePosition: (value: number) => void;
  setFps: (value: number) => void;
}

const defaultSeverityFilter: SeverityFilterState = {
  info: true,
  warn: true,
  error: true
};

export const useLogverseStore = create<LogverseState>((set, get) => ({
  logs: [],
  paused: false,
  selectedLog: null,
  activeService: null,
  severityFilter: defaultSeverityFilter,
  timelinePosition: 1,
  fps: 0,
  addLog: (log) => {
    const { paused } = get();
    if (paused) {
      return;
    }

    set((state) => {
      const nextLogs = [...state.logs, log];
      return {
        logs: nextLogs.slice(-MAX_LOGS)
      };
    });
  },
  togglePause: () => {
    set((state) => ({ paused: !state.paused }));
  },
  setSelectedLog: (log) => {
    set({ selectedLog: log });
  },
  setActiveService: (service) => {
    set({ activeService: service });
  },
  toggleSeverity: (severity) => {
    set((state) => ({
      severityFilter: {
        ...state.severityFilter,
        [severity]: !state.severityFilter[severity]
      }
    }));
  },
  showOnlySeverity: (severity) => {
    set({
      severityFilter: {
        info: severity === 'info',
        warn: severity === 'warn',
        error: severity === 'error'
      }
    });
  },
  setTimelinePosition: (value) => {
    set({ timelinePosition: Math.max(0, Math.min(1, value)) });
  },
  setFps: (value) => {
    set({ fps: value });
  }
}));

export const selectServiceList = (logs: LogEntry[]): string[] =>
  [...new Set(logs.map((log) => log.serviceName))].sort();

export const filterLogs = (
  logs: LogEntry[],
  activeService: string | null,
  severityFilter: SeverityFilterState
): LogEntry[] =>
  logs.filter((log) => {
    if (activeService && log.serviceName !== activeService) {
      return false;
    }

    return severityFilter[log.severity];
  });
